import React, { useState, useRef, useEffect } from "react";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  MicrophoneIcon,
  CpuChipIcon, // This icon is used for the AI bot.
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: "Hello! I am your AI assistant. How can I help you today?",
      sender: "bot",
    },
  ]);
  const [input, setInput] = useState("");
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to the latest message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (text) => {
    if (text.trim() === "") return;

    const newUserMessage = { text, sender: "user" };
    setMessages((prevMessages) => [...prevMessages, newUserMessage]);
    setInput("");

    // Simulate bot response after a delay
    setTimeout(() => {
      const botResponse = {
        text: `You said: "${text}". I am still learning, but I can help with common HR tasks!`,
        sender: "bot",
      };
      setMessages((prevMessages) => [...prevMessages, botResponse]);
    }, 1000);
  };

  const handleVoiceCommand = () => {
    // Check for browser support
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in your browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.start();
    setIsListening(true);

    recognition.onresult = (event) => {
      const speechToText = event.results[0][0].transcript;
      setInput(speechToText);
      handleSendMessage(speechToText);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
    };
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSendMessage(input);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[100]">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="w-80 h-96 bg-white shadow-2xl rounded-xl flex flex-col overflow-hidden" // Reduced height to h-96
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-gray-100 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <CpuChipIcon className="h-6 w-6 text-indigo-600" />
                <h3 className="font-bold text-gray-800">HRMS AI Assistant</h3>
              </div>
              <XMarkIcon
                className="h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-800 transition-colors"
                onClick={() => setOpen(false)}
              />
            </div>

            {/* Chat Messages */}
            <div className="flex-1 p-4 overflow-y-auto space-y-4 bg-gray-50 custom-scrollbar">
              {messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${
                    msg.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[75%] rounded-lg p-3 text-sm shadow-md ${
                      msg.sender === "user"
                        ? "bg-blue-500 text-white rounded-br-none"
                        : "bg-white text-gray-800 rounded-bl-none"
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input & Controls */}
            <div className="p-4 border-t border-gray-200 bg-white flex items-center space-x-2">
              <input
                type="text"
                placeholder={isListening ? "Listening..." : "Type a message..."}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 px-4 py-2 text-sm border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
                disabled={isListening}
              />
              <button
                onClick={handleVoiceCommand}
                className={`p-2 rounded-full transition-colors ${
                  isListening
                    ? "bg-red-500 text-white animate-pulse"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                <MicrophoneIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleSendMessage(input)}
                className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
              >
                <PaperAirplaneIcon className="h-5 w-5 -rotate-45" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      {!open && (
        <button
          className="bg-blue-600 p-4 rounded-full shadow-lg hover:bg-blue-700 transition"
          onClick={() => setOpen(true)}
        >
          <CpuChipIcon className="h-6 w-6 text-white" /> {/* New AI bot icon */}
        </button>
      )}
    </div>
  );
};

export default Chatbot;